import { useState, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Receipt, Users, Zap, Shield, Star, Utensils, Sparkles, ChevronDown, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import AnimatedBackground from '../components/AnimatedBackground';

const Landing = () => {
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const heroRef = useRef(null);
  
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.3], [1, 0.95]);

  const features = [
    {
      icon: Receipt,
      title: 'Smart Receipt Scanning',
      description: 'Snap a photo and let AI extract every item, taxes & tips automatically',
      gradient: 'from-orange-500 to-red-500',
      color: 'orange'
    },
    {
      icon: Users,
      title: 'Group Management',
      description: 'Create groups for roommates, trips, or regular dinners',
      gradient: 'from-emerald-500 to-teal-500',
      color: 'emerald'
    },
    {
      icon: Zap,
      title: 'Instant Splits',
      description: 'Calculate splits with CGST, SGST & tips in seconds',
      gradient: 'from-amber-500 to-orange-500',
      color: 'amber'
    },
    {
      icon: Shield,
      title: 'Multi-Currency',
      description: 'Support for INR, USD, GBP, EUR and more',
      gradient: 'from-blue-500 to-indigo-500',
      color: 'blue'
    }
  ];

  const stats = [
    { value: '10M+', label: 'Bills Split', delay: 0 },
    { value: '2M+', label: 'Happy Users', delay: 0.1 },
    { value: '₹500Cr+', label: 'Tracked', delay: 0.2 },
    { value: '4.9', label: 'App Rating', icon: Star, delay: 0.3 }
  ];

  const steps = [
    { step: '01', title: 'Scan Receipt', description: 'Take a photo or upload your bill' },
    { step: '02', title: 'Add Friends', description: 'Invite your group members' },
    { step: '03', title: 'Split & Send', description: 'AI calculates and notifies everyone' }
  ];

  return (
    <AnimatedBackground>
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 left-0 right-0 z-50"
      >
        <div className="mx-2 sm:mx-4 mt-2 sm:mt-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 glass-heavy rounded-xl sm:rounded-2xl">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
                <motion.div 
                  className="w-9 sm:w-11 h-9 sm:h-11 rounded-lg sm:rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg"
                  whileHover={{ scale: 1.05, rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Utensils className="w-4 sm:w-5 h-4 sm:h-5 text-white" />
                </motion.div>
                <span className="text-lg sm:text-2xl font-bold text-slate-800 font-['Poppins'] group-hover:text-orange-600 transition-colors">
                  BhojanSplit
                </span>
              </Link>
              
              <div className="hidden md:flex items-center gap-8">
                <a 
                  href="#features"
                  className="relative text-slate-600 hover:text-orange-600 transition-colors font-medium group"
                >
                  Features
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-500 to-red-500 group-hover:w-full transition-all duration-300" />
                </a>
                <a 
                  href="#how-it-works"
                  className="relative text-slate-600 hover:text-orange-600 transition-colors font-medium group"
                >
                  How it Works
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-500 to-red-500 group-hover:w-full transition-all duration-300" />
                </a>
              </div>
              
              <div className="flex items-center gap-2 sm:gap-3">
                <Link to="/login">
                  <Button variant="ghost" className="text-slate-700 hover:text-orange-600 hover:bg-orange-50 font-medium rounded-lg sm:rounded-xl text-sm sm:text-base px-3 sm:px-4" data-testid="nav-login-btn">
                    Log in
                  </Button>
                </Link>
                <Link to="/register" className="hidden sm:block">
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl px-4 sm:px-6 shadow-lg shadow-orange-500/25 font-medium text-sm sm:text-base" data-testid="nav-signup-btn">
                      Get Started
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </motion.div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6">
        <motion.div 
          style={{ opacity: heroOpacity, scale: heroScale }}
          className="max-w-7xl mx-auto w-full"
        >
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="text-center lg:text-left"
            >
              {/* Badge */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full glass mb-6 sm:mb-8"
              >
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                >
                  <Sparkles className="w-4 h-4 text-orange-500" />
                </motion.div>
                <span className="text-xs sm:text-sm text-slate-700 font-medium">AI-Powered Bill Splitting</span>
              </motion.div>
              
              {/* Heading */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold font-['Poppins'] leading-[1.1] mb-6 sm:mb-8">
                <span className="text-slate-800">Split bills,</span>
                <br />
                <span className="text-gradient-animated">share memories</span>
              </h1>
              
              {/* Description */}
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="text-base sm:text-lg md:text-xl text-slate-600 mb-8 sm:mb-10 leading-relaxed max-w-lg mx-auto lg:mx-0"
              >
                The smartest way to share bhojan expenses with friends. 
                Scan receipts, track GST, and settle up with ease. 
                <span className="font-medium text-slate-700"> Made for India! 🇮🇳</span>
              </motion.p>
              
              {/* CTA Buttons */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 }}
                className="flex flex-wrap gap-4 mb-8 sm:mb-12 justify-center lg:justify-start"
              >
                <Link to="/register">
                  <motion.button
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-premium flex items-center gap-2 text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4"
                  >
                    Start Splitting Free
                    <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5" />
                  </motion.button>
                </Link>
              </motion.div>

              {/* Stats */}
              <div className="flex flex-wrap gap-4 sm:gap-8 justify-center lg:justify-start">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 1 + stat.delay }}
                    className="text-center"
                  >
                    <div className="flex items-center gap-1">
                      <span className="text-2xl sm:text-3xl font-bold text-slate-800 font-['Poppins']">{stat.value}</span>
                      {stat.icon && <Star className="w-4 sm:w-5 h-4 sm:h-5 text-amber-500 fill-amber-500" />}
                    </div>
                    <span className="text-xs sm:text-sm text-slate-500">{stat.label}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Content - App Preview */}
            <motion.div
              initial={{ opacity: 0, x: 60, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative hidden sm:block"
            >
              {/* Glow Effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/20 via-amber-500/10 to-red-500/20 rounded-3xl blur-3xl opacity-60" />
              
              {/* Phone Mockup */}
              <motion.div 
                className="relative"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              >
                <div className="relative mx-auto w-full max-w-sm lg:max-w-md">
                  {/* App Card */}
                  <div className="premium-card p-4 sm:p-6 rounded-2xl sm:rounded-3xl">
                    <img 
                      src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&h=600&fit=crop"
                      alt="Restaurant"
                      className="w-full h-36 sm:h-48 object-cover rounded-xl sm:rounded-2xl mb-3 sm:mb-4"
                    />
                    <div className="flex justify-between items-start mb-3 sm:mb-4">
                      <div>
                        <h3 className="font-semibold text-slate-800 text-base sm:text-lg">Dinner at Taj Hotel</h3>
                        <p className="text-slate-500 text-xs sm:text-sm">Today, 8:30 PM</p>
                      </div>
                      <span className="text-xl sm:text-2xl font-bold text-gradient">₹4,280</span>
                    </div>
                    
                    {/* Avatars */}
                    <div className="flex items-center justify-between">
                      <div className="avatar-stack flex">
                        {['A', 'B', 'C', 'D'].map((letter, i) => (
                          <motion.div
                            key={letter}
                            initial={{ scale: 0, x: -20 }}
                            animate={{ scale: 1, x: 0 }}
                            transition={{ delay: 1.2 + i * 0.1 }}
                            className={`w-8 sm:w-10 h-8 sm:h-10 rounded-full flex items-center justify-center text-white font-medium text-xs sm:text-sm shadow-lg ${
                              ['bg-orange-500', 'bg-emerald-500', 'bg-blue-500', 'bg-purple-500'][i]
                            }`}
                          >
                            {letter}
                          </motion.div>
                        ))}
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 1.6 }}
                          className="w-8 sm:w-10 h-8 sm:h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-medium text-xs"
                        >
                          +2
                        </motion.div>
                      </div>
                      <motion.span 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.8 }}
                        className="text-xs sm:text-sm text-emerald-600 font-medium flex items-center gap-1"
                      >
                        <Check className="w-3 sm:w-4 h-3 sm:h-4" />
                        Split Sent
                      </motion.span>
                    </div>
                  </div>

                  {/* Floating notification */}
                  <motion.div
                    initial={{ opacity: 0, x: 50, y: 20 }}
                    animate={{ opacity: 1, x: 0, y: 0 }}
                    transition={{ delay: 2, duration: 0.6 }}
                    className="absolute -right-2 sm:-right-4 top-16 sm:top-20 glass p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-xl max-w-[160px] sm:max-w-[200px]"
                  >
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                        <Check className="w-4 sm:w-5 h-4 sm:h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-slate-800">Raj paid</p>
                        <p className="text-xs text-slate-500">₹1,070</p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center gap-2 text-slate-400"
          >
            <span className="text-xs font-medium">Scroll to explore</span>
            <ChevronDown className="w-5 h-5" />
          </motion.div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12 sm:mb-16 lg:mb-20"
          >
            <span className="inline-block text-orange-600 font-medium text-xs sm:text-sm tracking-wider uppercase mb-3 sm:mb-4">Features</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-['Poppins'] text-slate-800 mb-4 sm:mb-6">
              Everything you need
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto px-4">
              Powerful features designed for Indian restaurants & bills
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onMouseEnter={() => setHoveredFeature(index)}
                onMouseLeave={() => setHoveredFeature(null)}
                className="group"
              >
                <div className="premium-card card-glow p-6 sm:p-8 h-full cursor-pointer">
                  <motion.div
                    animate={hoveredFeature === index ? { scale: 1.1, rotate: 5 } : { scale: 1, rotate: 0 }}
                    className={`w-12 sm:w-14 h-12 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 sm:mb-6 shadow-lg`}
                  >
                    <feature.icon className="w-6 sm:w-7 h-6 sm:h-7 text-white" />
                  </motion.div>
                  <h3 className="text-lg sm:text-xl font-semibold text-slate-800 mb-2 sm:mb-3 group-hover:text-orange-600 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12 sm:mb-16 lg:mb-20"
          >
            <span className="inline-block text-orange-600 font-medium text-xs sm:text-sm tracking-wider uppercase mb-3 sm:mb-4">How it Works</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-['Poppins'] text-slate-800 mb-4 sm:mb-6">
              Split in 3 easy steps
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="relative"
              >
                {index < 2 && (
                  <div className="hidden md:block absolute top-16 left-full w-full h-px bg-gradient-to-r from-orange-300 to-transparent -translate-x-1/2 z-0" />
                )}
                <div className="premium-card p-6 sm:p-8 text-center relative z-10">
                  <div className="text-5xl sm:text-6xl font-bold text-gradient opacity-20 font-['Poppins'] mb-3 sm:mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-slate-800 mb-2 sm:mb-3">{step.title}</h3>
                  <p className="text-sm sm:text-base text-slate-600">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Glow */}
            <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/20 via-amber-500/20 to-red-500/20 rounded-[32px] sm:rounded-[40px] blur-3xl" />
            
            <div className="relative premium-card p-8 sm:p-12 md:p-16 text-center rounded-[24px] sm:rounded-[32px] overflow-hidden">
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-48 sm:w-64 h-48 sm:h-64 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 sm:w-64 h-48 sm:h-64 bg-gradient-to-tr from-amber-500/10 to-transparent rounded-full blur-3xl" />
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="relative z-10"
              >
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-['Poppins'] text-slate-800 mb-4 sm:mb-6">
                  Ready to split smarter?
                </h2>
                <p className="text-base sm:text-lg text-slate-600 mb-8 sm:mb-10 max-w-xl mx-auto">
                  Join millions of Indians who&apos;ve simplified their bill splitting with BhojanSplit.
                </p>
                <Link to="/register">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn-premium text-base sm:text-lg px-8 sm:px-10 py-3 sm:py-4"
                  >
                    Get Started Free
                    <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5 ml-2 inline" />
                  </motion.button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 px-4 sm:px-6 border-t border-slate-200/50">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
              <Utensils className="w-4 sm:w-5 h-4 sm:h-5 text-white" />
            </div>
            <span className="text-lg sm:text-xl font-bold text-slate-800 font-['Poppins']">BhojanSplit</span>
          </div>
          <p className="text-slate-500 text-xs sm:text-sm text-center sm:text-left">
            © 2026 BhojanSplit. Made with ❤️ in India
          </p>
        </div>
      </footer>
    </AnimatedBackground>
  );
};

export default Landing;
